/**
 * CRM cross-project: query unificata di TUTTE le attività dell'organization
 * con join su project + customer per popolare la pagina Agenda e i widget
 * dashboard "Follow-up imminenti".
 *
 * NB: separato da useProjectActivities per evitare circular-dep e per usare
 * un cache-key dedicato che cambia con i filtri.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import type { ProjectActivity, ProjectActivityType } from './useProjectActivities';

export interface AllActivitiesFilters {
  /** Solo attività con follow_up_at non NULL e ancora pendenti (contact_at NULL o futuro). */
  onlyPending?: boolean;
  /** Limita ai prossimi N giorni dal now (sul follow_up_at). null = nessun limite alto. */
  upcomingDays?: number | null;
  /** Tipi inclusi (vuoto = tutti). */
  types?: ProjectActivityType[];
  /** Limita a un singolo progetto. */
  projectId?: string;
  /** Limita a un singolo cliente. */
  customerId?: string;
  /** Limite righe da scaricare (default 200, max 500). */
  limit?: number;
}

export interface EnrichedActivity extends ProjectActivity {
  project: {
    id: string;
    name: string;
    client: string | null;
    customer_id: string | null;
    customer_name: string | null;
  } | null;
}

export function useAllActivities(filters: AllActivitiesFilters = {}) {
  const { currentOrganizationId } = useCurrentOrganization();

  const queryKey = [
    'all-activities',
    currentOrganizationId,
    filters.onlyPending ?? false,
    filters.upcomingDays ?? null,
    (filters.types ?? []).join(','),
    filters.projectId ?? null,
    filters.customerId ?? null,
    filters.limit ?? 200,
  ];

  return useQuery<EnrichedActivity[]>({
    queryKey,
    enabled: !!currentOrganizationId,
    staleTime: 30_000,
    queryFn: async () => {
      if (!currentOrganizationId) return [];

      // Embed esplicito su projects + customers per evitare PGRST201 FK ambigua.
      // Usiamo la FK canonica project_activities_project_id_fkey definita nella migration.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let query = (supabase as any)
        .from('project_activities')
        .select(`
          *,
          estimates ( id, name, version ),
          projects!project_activities_project_id_fkey (
            id, name, client, customer_id,
            customers ( name )
          )
        `)
        .eq('organization_id', currentOrganizationId);

      if (filters.projectId) {
        query = query.eq('project_id', filters.projectId);
      }
      if (filters.types && filters.types.length > 0) {
        query = query.in('type', filters.types);
      }
      if (filters.onlyPending) {
        // Un'attività è "pendente" se almeno una delle due task è da fare:
        //   contatto programmato e non ancora fatto, oppure
        //   follow-up programmato e non ancora completato.
        query = query.or(
          'and(contact_at.not.is.null,contact_done_at.is.null),and(follow_up_at.not.is.null,completed_at.is.null)'
        );
      }
      if (filters.upcomingDays != null) {
        const horizon = new Date();
        horizon.setDate(horizon.getDate() + filters.upcomingDays);
        query = query.lte('follow_up_at', horizon.toISOString());
      }

      query = query
        .order('follow_up_at', { ascending: true, nullsFirst: false })
        .order('contact_at', { ascending: false, nullsFirst: true })
        .limit(Math.min(filters.limit ?? 200, 500));

      const { data, error } = await query;
      if (error) {
        console.error('[useAllActivities] error:', error);
        throw error;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const enriched: EnrichedActivity[] = (data ?? []).map((r: any) => ({
        ...r,
        project: r.projects ? {
          id: r.projects.id,
          name: r.projects.name,
          client: r.projects.client ?? null,
          customer_id: r.projects.customer_id ?? null,
          customer_name: r.projects.customers?.name ?? null,
        } : null,
      }));

      // Customer filter applicato client-side (PostgREST embed non supporta where su nested).
      if (filters.customerId) {
        return enriched.filter(a => a.project?.customer_id === filters.customerId);
      }
      return enriched;
    },
  });
}

/**
 * Hook specializzato per il widget dashboard "Follow-up imminenti".
 * Solo reminder pendenti nei prossimi N giorni.
 */
export function useFollowUpImminent(days: number = 7) {
  return useAllActivities({
    onlyPending: true,
    upcomingDays: days,
    limit: 20,
  });
}
